import { useQuery } from "@tanstack/react-query";
import { Link, Navigate } from "react-router-dom";

import { api } from "../api";
import { ActivityItem } from "../components/Activity";
import { GetTheApp } from "../components/GetTheApp";
import { LeagueCurrent } from "../components/LeagueCurrent";
import type { ActivityRow } from "../components/Activity";
import { useSession } from "../session";
import { Logo } from "../shell/Brand";
import { Card } from "../shell/ui";

interface Stats {
  members: number;
  strategies: number;
  backtests: number;
  bots: number;
  trades: number;
  verified: number;
  comments: number;
}

const PILLARS = [
  {
    title: "Build without code",
    body: "Pick indicators, combine conditions, set your risk. The same builder as the desktop app, in your browser.",
  },
  {
    title: "Check the numbers yourself",
    body: "Every published result carries the engine version and a strategy hash. Re-run it with one click and see it match — or see the bar where it didn't.",
  },
  {
    title: "Share, rate, discuss",
    body: "Publish a strategy, fork someone else's, and follow the bots you find interesting.",
  },
];

/** The front door for guests. Signed-in members go straight to their own space. */
export function HomePage() {
  const { user, isLoading } = useSession();
  const stats = useQuery({
    queryKey: ["stats"],
    queryFn: () => api<Stats>("/hub/stats"),
    refetchInterval: 30_000,
    enabled: !isLoading && !user,
  });
  const feed = useQuery({
    queryKey: ["feed", "home"],
    queryFn: () => api<{ items: ActivityRow[] }>("/hub/feed?limit=12"),
    refetchInterval: 20_000,
    enabled: !isLoading && !user,
  });
  if (isLoading) return null;
  if (user) return <Navigate to="/me" replace />;

  return (
    <div className="landing">
      <section className="landing__hero">
        <Logo size={56} />
        <h1 className="landing__title">Trade history before you trade money.</h1>
        <p className="landing__lede">
          StrategyHive is a place to build trading strategies, test them against real market history, run them
          as paper bots, and share what you find — where every number can be checked by anyone.
        </p>
        <div className="row landing__cta">
          <Link className="btn btn--primary" to="/signin">
            Try it — one click, no sign-up
          </Link>
          <Link className="btn" to="/explore">
            Look around first
          </Link>
          <Link className="btn" to="/download">
            ⬇ Download the app
          </Link>
        </div>
        <p className="muted small">
          Paper trading only. Nothing here is investment advice, and no exchange keys are ever asked for.
        </p>
      </section>
      <GetTheApp />
      {stats.data && (
        <section className="landing__stats" aria-label="StrategyHive right now">
          {(
            [
              [stats.data.members, "member", "members"],
              [stats.data.strategies, "published strategy", "published strategies"],
              [stats.data.backtests, "backtest run", "backtests run"],
              [stats.data.bots, "paper bot live now", "paper bots live now"],
              [stats.data.verified, "result re-run and verified", "results re-run and verified"],
              [stats.data.comments, "comment", "comments"],
            ] as const
          ).map(([n, one, many]) => (
            <div key={many} className="landing__stat">
              <strong>{n.toLocaleString()}</strong>
              <span className="muted">{n === 1 ? one : many}</span>
            </div>
          ))}
        </section>
      )}
      <section className="landing__pillars">
        {PILLARS.map((p) => (
          <div key={p.title} className="card landing__pillar">
            <div className="card__body">
              <h2>{p.title}</h2>
              <p className="muted">{p.body}</p>
            </div>
          </div>
        ))}
      </section>
      <LeagueCurrent />
      <Card
        title="Happening now"
        actions={
          <Link className="btn btn--sm btn--ghost" to="/explore">
            Explore
          </Link>
        }
        flush
      >
        <ul className="activity-list">
          {feed.data?.items.map((a) => (
            <ActivityItem key={a.id} a={a} />
          ))}
        </ul>
      </Card>
    </div>
  );
}
